
import Vue, {VueConstructor} from 'vue'
import FilterStateMixin from '~/mixins/filter-state.mixin'
import {ListingDetailInterface, ChefPricingInterface, ChefPricingRequestInterface} from '~/store/listing'
import ResponsiveMixin from '~/mixins/responsive.mixin'
import QueryMixin from '~/mixins/query.mixin'
import {formatInputData} from '~/util/format-filter-data'
import {mapGetters} from 'vuex'
import ContactCard from "~/components/ui/contact-card.vue";
import ChefCard from "~/components/index/chef-highlight/chef-card.vue";
import {EVENT_CHANGE_ADDRESS, EVENT_CHANGE_DATA} from "~/util/event-bus";
import {ExtraSelectedInterface} from "~/components/chef/extra-card.vue";

import '/assets/checkbox.css';
import moment from "moment/moment";

export interface bookingRequestInterface {
  testing?: boolean;
  coupon?: string;
  comment?: string;
  address: {
    street: string;
    houseNumber: string;
    addition: string;
    country: string;
    city: string;
    zipcode: string;
  };
  vat_invoice: boolean;
  vat_company_name?: string;
  vat_number?: string;
  vat_company_address_is_booking_address: boolean;
  vat_company_address?: {
    street: string;
    houseNumber: string;
    addition: string;
    country: string;
    city: string;
    zipcode: string;
  };
  guests: number;
  date: string;
  booker: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  extras: { id: number; quantity: number }[];
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof FilterStateMixin> & InstanceType<typeof ResponsiveMixin> & InstanceType<typeof QueryMixin>>).extend({
  name: 'ListingBook',
  components: {ChefCard, ContactCard},
  mixins: [FilterStateMixin, ResponsiveMixin, QueryMixin],
  head() {
    return {
      bodyAttrs: {
        class: 'bg-white text-offBlack'
      },
      title: this.$data.seoTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.$data.seoDescriptionMeta,
        }
      ],
    }
  },
  data(): {
    seoTitle: string,
    seoDescriptionMeta: string,
    form: {
      coupon: bookingRequestInterface["coupon"],
      comment: bookingRequestInterface["comment"],
      address: bookingRequestInterface["address"],
      vat_invoice: bookingRequestInterface["vat_invoice"],
      vat_company_name: bookingRequestInterface["vat_company_name"]
      vat_number: bookingRequestInterface["vat_number"]
      vat_company_address_is_booking_address: bookingRequestInterface["vat_company_address_is_booking_address"],
      vat_company_address: bookingRequestInterface["vat_company_address"],
      time: string,
      booker: bookingRequestInterface["booker"],
    },
    loading: boolean,
    calculateChefPriceTimeout: any,
    privacyAccepted: boolean,
    step: number,
    endCheckoutSent: boolean
  } {
    return {
      seoTitle: '',
      seoDescriptionMeta: '',
      calculateChefPriceTimeout: 0,
      loading: false,
      privacyAccepted: true,
      form: {
        coupon: "",
        comment: "",
        address: {
          street: "",
          houseNumber: "",
          addition: "",
          country: "NLD",
          city: "",
          zipcode: "",
        },
        vat_invoice: false,
        vat_company_name: undefined,
        vat_number: "",
        vat_company_address_is_booking_address: true,
        vat_company_address: {
          street: "",
          houseNumber: "",
          addition: "",
          country: "NLD",
          city: "",
          zipcode: "",
        },
        time: '',
        booker: {
          first_name: "",
          last_name: "",
          email: "",
          phone: ""
        }
      },
      step: 1,
      endCheckoutSent: false,
    }
  },
  computed: {
    ...mapGetters({
      filter: 'filter/filter',
      extraById: 'listing/extraByListingId'
    }),
    commentPlaceHolder(): string {
      // Fix for showing custom text for CHEFSTBL specials - BBQ
      if (this.listing?.chef?.id === 3183 && this.listing?.subtitle.toUpperCase().includes('BBQ')) {
        return 'Als je zelf een goede BBQ hebt waar de chef gebruik van kan maken, kan je dat ook hier aangeven.';
      }

      return 'Geef hier aan indien de chef rekening moet houden met dieetwensen of allergieën (optioneel)'
    },
    chefIdOrSlug(): string {
      return this.$route.params.id
    },
    chefId(): string {
      return (this.listing?.id ?? 0).toString(10)
    },
    listing(): ListingDetailInterface {
      return this.$store.getters['listing/byIdOrSlug'](this.chefIdOrSlug)
    },
    price(): ChefPricingInterface {
      return this.$store.getters['listing/pricingByListingId'](this.listing?.id)
    },
    selectedExtras(): { id: number, quantity: number }[] {
      return ((this.extraById(this.chefId) ?? []) as ExtraSelectedInterface[]).filter(option => option && option.value > 0).map(option => {
        return {
          id: option.id,
          quantity: option.value,
        }
      })
    },
    readyToFetchPrice(): boolean {
      const validNumberOfPersons = this.filter.person > 0 && this.filter.person >= this.listing.min_guests && this.filter.person <= this.listing.max_guests;
      return this.filter.address && validNumberOfPersons && this.filter.calendar;
    },
    blockedDates(): Date[] {
      // @ts-ignore
      const blockedDates = this.listing?.chef.blocked_dates ?? []
      const map = blockedDates.map((date: string) => moment(date).toDate())

      if (this.listing?.booking_days_in_advance) {
        // We start at 0, so "1 day in advance" is block today
        for (let i = 0; i < this.listing.booking_days_in_advance; i++) {
          const date = moment().add(i, 'days');
          map.push(date.toDate());
        }
      }

      return map;
    }
  },
  async mounted() {
    if (this.filters.address) {
      this.form.address = {
        street: this.filters.address.street,
        houseNumber: this.filters.address.houseNumber,
        addition: this.filters.address.addition,
        country: this.filters.address.country_code,
        city: this.filters.address.city,
        zipcode: this.filters.address.zipcode,
      }
    }

    if (this.form.coupon) {
      this.checkCoupon()
    }

    this.$nuxt.$on(EVENT_CHANGE_ADDRESS, () => {
      if (this.filters.address) {
        // Only update fields that are not shown in UI
        this.form.address.city = this.filters.address.city
        this.form.address.country = this.filters.address.country_code
      }
    });

    this.$nuxt.$on(EVENT_CHANGE_DATA, () => {
      if (this.calculateChefPriceTimeout) {
        clearTimeout(this.calculateChefPriceTimeout)
      }

      if (this.readyToFetchPrice) {
        this.calculateChefPriceTimeout = setTimeout(async () => {
          await this.$store.dispatch('listing/calculateChefPrice', {
            id: this.chefId,
            data: {
              coupon: this.form.coupon,
              ...formatInputData(this.filter),
              extras: (this.selectedExtras ?? []).map(option => {
                return {
                  id: option.id,
                  quantity: option.quantity,
                }
              })
            } as ChefPricingRequestInterface
            // @ts-ignore
          }).catch(error => {
            let errorMessage = error.response?.data?.message ?? 'Er ging iets mis'

            // todo very ugly
            if (errorMessage === 'Je locatie is buiten het werkgebied van de chef') {
              this.$store.commit('filter/UPDATE_FILTERS', {
                address: null,
              })
            }

            if (error.response?.data?.errors.hasOwnProperty('coupon')) {
              this.form.coupon = '';
              errorMessage = error.response?.data?.errors?.coupon;
            }

            alert(errorMessage)
          })
        }, 500);
      }
    });

    setTimeout(() => {
      if (this.readyToFetchPrice && this.price) {
        this.$gtm.push({
          event: 'begin_checkout',
          value: this.price.total_in_cents / 100,
          currency: "EUR",
          items: [{
            item_id: this.listing.id,
            item_name: this.listing.title + ' | ' + this.listing.subtitle,
            price: this.price.total_in_cents / 100, // we register this, for now, as single item instead of breakdown
            quantity: this.price.arrangements.quantity,
          }]
        });
      }
    }, 1000);
  },
  methods: {
    checkCoupon: function () {
      this.$nuxt.$emit(EVENT_CHANGE_DATA, {});
    },
    storeBooker: async function () {
      try {
        await this.$axios.$post(`/v1/listing/${this.chefId}/booker`, {
          booker: this.form.booker,
        })
      } catch (e) {
        console.error(e);
      }
    },
    nextStep: function () {
      // Could already store details in DB here
      this.step = 2;

      this.storeBooker();

      if (!this.endCheckoutSent) {
        this.$gtm.push({
          event: 'end_checkout',
          value: this.price.total_in_cents / 100,
          currency: "EUR",
          items: [{
            item_id: this.listing.id,
            item_name: this.listing.title + ' | ' + this.listing.subtitle,
            price: this.price.total_in_cents / 100, // we register this, for now, as single item instead of breakdown
            quantity: this.price.arrangements.quantity,
          }]
        });

        this.endCheckoutSent = true;
      }

      window.scrollTo(0, 0);
    },
    previousStep: async function () {
      this.step = 1;

      window.scrollTo(0, 0);
    },
    submitBooking: async function () {
      this.loading = true;

      const date = moment(this.filter.calendar).format('YYYY-MM-DD')

      const extras: bookingRequestInterface["extras"] = (this.selectedExtras ?? []).map(option => {
        return {
          id: option.id,
          quantity: option.quantity,
        }
      })

      let vatCompanyAddress = undefined;
      if (this.form.vat_invoice) {
        vatCompanyAddress = this.form.vat_company_address_is_booking_address ? this.form.address : this.form.vat_company_address;
      }

      const bookingRequest: bookingRequestInterface = {
        // testing: true,
        coupon: this.form.coupon,
        comment: this.form.comment,
        address: this.form.address,
        vat_invoice: this.form.vat_invoice,
        vat_company_name: this.form.vat_company_name,
        vat_number: this.form.vat_number,
        vat_company_address_is_booking_address: this.form.vat_company_address_is_booking_address,
        vat_company_address: vatCompanyAddress,
        guests: this.filter.person,
        date: date + "\T" + this.form.time,
        booker: this.form.booker,
        extras: extras,
      }

      // @ts-ignore
      await this.$axios.$post(`/v1/listing/${this.chefId}/book`, bookingRequest).then(response => {
        const reservationId = response.id;

        // Combination of UA and GA4 (duplicate) keys
        this.$gtm.push({
          event: 'purchase',
          transaction_id: reservationId,
          value: this.price.total_in_cents / 100,
          currency: 'EUR',
          items: [{
            item_id: this.listing.id,
            item_name: this.listing.title + ' | ' + this.listing.subtitle,
            price: this.price.total_in_cents / 100, // we register this, for now, as single item instead of breakdown
            //price: this.price.arrangements.total_in_cents / 100,
            quantity: this.price.arrangements.quantity,
          }],
          // UA (legacy)
          'transactionId': reservationId,
          'transactionTotal': this.price.total_in_cents / 100,
          'transactionCurrency': 'EUR',
          'transactionProducts': [{
            'sku': this.listing.id,
            'name': this.listing.title + ' | ' + this.listing.subtitle,
            'price': this.price.total_in_cents / 100,
            'quantity': this.price.arrangements.quantity,
          }]
        });

        this.$router.push({
          path: `/chef/${this.chefIdOrSlug}/requested/${reservationId}/`,
        })
        // @ts-ignore
      }).catch(error => {
        if (error.response) {
          console.log(error.response)
        }

        let errorMessage = 'Er is een onverwachte fout opgetreden. Controleer het formulier en probeer het opnieuw.';
        if (error.response?.data?.errors.hasOwnProperty('booker.email')) {
          errorMessage = 'Email moet een geldig e-mailadres zijn. Controleer het formulier en probeer het opnieuw.';
        }

        alert(errorMessage);
      }).finally(() => {
        this.loading = false;
      });
    }
  },
  async fetch() {
    await this.$store.dispatch('listing/fetchListing', {
      id: this.chefIdOrSlug,
    })

    let {
      address,
      calendar,
      person,
      dynamicFilters,
      selectedExtras
    } = await this.parseQueryParams(this.$route.query as { [key: string]: string })

    // generic function to validate/filter/clean parsed query params based on $chef?
    // e.g. min/max guests, selected date
    if (calendar) {
      const formattedDate = moment(calendar).format('YYYY-MM-DD');
      const blockedDates = this.listing?.chef.blocked_dates ?? [];
      if (blockedDates.indexOf(formattedDate) !== -1) {
        calendar = undefined;
      }
    }

    this.$store.commit('filter/UPDATE_FILTERS', {
      address,
      calendar,
      person,
      dynamicFilters
    })

    this.seoTitle = `Aanvragen ${this.listing.title} | ${this.listing.subtitle}`;
    this.seoDescriptionMeta = `Reserveer hier direct ${this.listing.title} | ${this.listing.subtitle}`;

    if (this.readyToFetchPrice) {
      await this.$store.dispatch('listing/calculateChefPrice', {
        id: this.chefId,
        data: {
          ...formatInputData(this.filter),
          coupon: '',
          // @ts-ignore
          extras: (selectedExtras ?? []).map(option => {
            return {
              id: option.id,
              quantity: option.quantity,
            }
          })
        } as ChefPricingRequestInterface
      }).catch(() => {
        // Reset all filters, something wrong with input
        this.$store.commit('filter/UPDATE_FILTERS', {
          calendar: null,
          person: null,
          selectedExtras: null,
          address: null,
        })
      })
    }

    this.$store.commit('listing/UPDATE_SELECTED_EXTRA', {
      key: this.chefId,
      // @ts-ignore
      data: (selectedExtras ?? []).map(option => ({
        id: option.id,
        value: option.quantity
      }))
    })
  },
  beforeDestroy() {
    this.$nuxt.$off(EVENT_CHANGE_ADDRESS)
    this.$nuxt.$off(EVENT_CHANGE_DATA)
  },
  fetchKey: 'chef-price'
})
